import { inject, observer } from 'mobx-react';
import { RootStore } from 'store/root.store';
import * as React from 'react';
import * as Styled from './styled';
import TimerNewDialogStore from 'store/Timer/timer.new.dialog.store';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    TextField
} from '@material-ui/core';
import { FlexDiv } from 'common/flex';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import { ApiConsumer } from 'common/ApiProvider';
import RootAPI from '../../api/interfaces/RootAPI';
import Template from '../../api/immutables/ImmutableTemplate';
import AutoCompleteField from 'components/AutoCompleteField';
import { MatterItemFormatter } from 'components/TemplateForm';
import { Features, Matter, Client } from '../../api/types/types';
import { withTranslation } from 'react-i18next';
import { getMatterTooltipText } from 'util/timeEntry';
import { Clear } from '@material-ui/icons';

interface Props {
    dialogStore?: TimerNewDialogStore;
    // tslint:disable-next-line:no-any
    t: any;
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        dialogStore: rootStore.newTimerDialogStore
    };
})
@observer
class NewTimerDialog extends React.Component<Props> {
    fetchMatters = (api: RootAPI, clientId?: number) => async (searchText: string, offset: number, limit: number, features: Features) => {
        let results: Matter[] = await api.Matter.searchMatters(searchText, true, clientId, undefined, offset, limit);
        if (!features.EpochConfigTrackedMatterClientsEnabled && results.length === 0) {
            results = await api.Matter.getAvailableMatters(searchText, false, clientId, undefined, offset, limit);
        }
        return results;
    }
    render() {
        const { t, dialogStore } = this.props;
        const {
            isOpen,
            template,
            setTemplate,
            client,
            setClient,
            setMatter,
            matter,
            name,
            setName,
            cancel,
            doSave,
            start,
            toggleStart,
            validation,
            saving,
            handleEventType,
            rootStore
        } = dialogStore!;
        const { isFromTransferDialog } = rootStore.transferTimerDialogStore;
        const { mainWinLoading } = rootStore.timerStore;
        const fetchTemplates = rootStore.templateStore.fetchTemplatesInAutoComplate;
        const matterLabel = rootStore.appStore.features.EpochConfigMatterLabel;
        const handleSave = async (e: React.MouseEvent) => {
            handleEventType(e);
            if (isFromTransferDialog) {
                await rootStore.transferTimerDialogStore.transferChunks();
            } else {
                await doSave();
            }
        };
        return (
            <Dialog
                PaperProps={{style: {overflow: 'visible', maxWidth: '700px'}}}
                disableBackdropClick={true}
                open={isOpen}
                fullWidth={true}
                maxWidth={false}
                onClose={cancel}
                disableRestoreFocus={true}
            >
                <DialogTitle
                    id="timer-dialog-title"
                    style={{paddingBottom: 0}}
                >
                    {t('dialog.new_timer.title')}
                </DialogTitle>
                <DialogContent>
                    <FlexDiv direction="column">
                        <Styled.PaddedFormItem>
                            <TextField
                                value={name}
                                label={t('dialog.new_timer.field.name')}
                                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setName(evt.target.value)}
                                fullWidth={true}
                                error={!!validation.name}
                                helperText={validation.name ? t(validation.name) : undefined}
                                disabled={mainWinLoading}
                                InputProps={{
                                    endAdornment: (name && !mainWinLoading) ? (
                                        <InputAdornment
                                            position="end"
                                            onClick={() => setName('')}
                                        >
                                            <Clear style={{ width: 20, cursor: 'pointer' }} />
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Styled.PaddedFormItem>
                        <FeaturesConsumer>
                            {(features: Features) =>
                                <ApiConsumer>
                                    {(api: RootAPI) =>
                                        <>
                                            <Styled.PaddedFormItem>
                                                <AutoCompleteField
                                                    label={t('dialog.new_timer.field.template')}
                                                    fetch={fetchTemplates}
                                                    currentItem={template}
                                                    clearable={true}
                                                    disabled={mainWinLoading}
                                                    onClear={() => setTemplate(undefined)}
                                                    getItemText={(m: Template) => `${m.name}`}
                                                    onSelect={setTemplate}
                                                />
                                            </Styled.PaddedFormItem>
                                            <Styled.Fields>
                                                <Styled.PaddedFormItem>
                                                    <AutoCompleteField
                                                        label={t('dialog.new_timer.field.client')}
                                                        fetch={api.Client.searchClients}
                                                        currentItem={client}
                                                        clearable={true}
                                                        onClear={() => setClient()}
                                                        getItemText={(c: Client) => `${c.name} - ${c.number}`}
                                                        onSelect={setClient}
                                                    />
                                                </Styled.PaddedFormItem>
                                                <Styled.PaddedFormItem>
                                                    <AutoCompleteField
                                                        label={t('dialog.new_timer.field.matter', { matterLabel })}
                                                        fetch={(...props) => this.fetchMatters(api, client?.id)(...props, features)}
                                                        currentItem={matter}
                                                        clearable={true}
                                                        onClear={() => setMatter()}
                                                        formatItem={MatterItemFormatter}
                                                        getItemText={(m: Matter) => `${m.name} - ${m.number}`}
                                                        onSelect={setMatter}
                                                        tooltip={getMatterTooltipText}
                                                        disabled={mainWinLoading}
                                                    />
                                                </Styled.PaddedFormItem>
                                            </Styled.Fields>
                                        </>
                                    }
                                </ApiConsumer>
                            }
                        </FeaturesConsumer>
                        {!isFromTransferDialog && (
                            <Styled.PaddedFormItem>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disabled={mainWinLoading}
                                            checked={start}
                                            onChange={toggleStart}
                                        />
                                    }
                                    label={t('dialog.new_timer.field.start_checkbox')}
                                />
                            </Styled.PaddedFormItem>
                        )}
                    </FlexDiv>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={rootStore.transferTimerDialogStore.saving || saving || mainWinLoading || (!!client && !matter)}
                        onClick={handleSave}
                    >
                        {t('save', { ns: 'common' })}
                    </Button>
                    <Button
                        disabled={mainWinLoading}
                        onClick={(e: React.MouseEvent) => {
                            handleEventType(e);
                            cancel();
                        }}
                    >
                        {t('cancel', { ns: 'common' })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default withTranslation(['timers', 'common'])(NewTimerDialog);